import { useId, useMemo, useState } from 'react';
import type { PerformancePoint } from '../lib/api';
import { credits } from '../lib/format';

/**
 * Cumulative realised profit, one step per settled market.
 *
 * Form: change-over-time, one series → a line. One series means no legend; the
 * heading names it.
 *
 * **Why this is not a green-up / red-down chart.** Running the app's status
 * colours through the palette validator against this surface gives a
 * colourblind separation of ΔE 4.1 for deuteranopia — far below the floor of 8.
 * A reader with the most common form of colour blindness would not be able to
 * tell a winning run from a losing one. So polarity is carried by *position*
 * against a labelled zero line, which every reader can see, and the line itself
 * is a single hue (categorical slot 1, the same blue as the price chart).
 *
 * The signed total above the chart repeats the answer in text with an explicit
 * + or −, so the direction is never conveyed by colour alone.
 *
 * A step line rather than a smooth one: profit changes at the instant a market
 * settles and is flat in between. Interpolating between settlements would draw
 * a gradual climb that never happened.
 */
export function PnlChart({ points, height = 200 }: { points: PerformancePoint[]; height?: number }) {
  const gradientId = useId();
  const [hover, setHover] = useState<number | null>(null);

  const series = useMemo(
    () => points.map((p) => ({ ...p, value: BigInt(p.cumulative) })),
    [points],
  );

  if (series.length === 0) {
    return (
      <div className="empty-state">
        No settled markets yet. Your record appears here once a market you traded resolves.
      </div>
    );
  }

  const W = 600;
  const H = height;
  const PAD = { top: 12, right: 62, bottom: 22, left: 4 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  // The domain always includes zero, so the baseline is on screen even for
  // someone who has only ever lost. Padded by a tenth so the line never runs
  // along the very edge of the panel.
  const values = series.map((p) => p.value);
  const rawMax = values.reduce((a, b) => (b > a ? b : a), 0n);
  const rawMin = values.reduce((a, b) => (b < a ? b : a), 0n);
  const span = rawMax - rawMin;
  const pad = span === 0n ? 100n : span / 10n;
  const max = rawMax + pad;
  const min = rawMin - pad;
  const range = max - min === 0n ? 1n : max - min;

  // bigint → screen space. Scaled in integers first to keep the precision.
  const y = (v: bigint): number => {
    const frac = Number(((max - v) * 10_000n) / range) / 10_000;
    return PAD.top + frac * plotH;
  };
  const x = (i: number): number =>
    PAD.left + (series.length === 1 ? plotW / 2 : (i / (series.length - 1)) * plotW);

  const zeroY = y(0n);

  // Step path: hold the previous level, then jump at the settlement.
  const path = series
    .map((p, i) => {
      const px = x(i).toFixed(2);
      const py = y(p.value).toFixed(2);
      if (i === 0) return series.length === 1 ? `M${PAD.left},${py} L${px},${py}` : `M${px},${py}`;
      return `L${px},${y(series[i - 1]!.value).toFixed(2)} L${px},${py}`;
    })
    .join(' ');

  const lastX = x(series.length - 1).toFixed(2);
  const firstX = series.length === 1 ? PAD.left.toFixed(2) : x(0).toFixed(2);
  const area = `${path} L${lastX},${zeroY.toFixed(2)} L${firstX},${zeroY.toFixed(2)} Z`;

  const active = hover === null ? null : series[hover];
  const latest = series[series.length - 1]!;
  // Drop any tick that would land on top of the break-even line. The zero line
  // is labelled in the plot, and two labels sharing eight pixels of gutter is
  // worse than one tick fewer.
  const ticks = [max, (max + min) / 2n, min].filter((t) => Math.abs(y(t) - y(0n)) > 14);

  return (
    <div className="chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Cumulative realised profit across ${series.length} settled market${
          series.length === 1 ? '' : 's'
        }. Currently ${latest.value < 0n ? 'down' : 'up'} ${credits(
          latest.value < 0n ? -latest.value : latest.value,
        )} credits.`}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const box = e.currentTarget.getBoundingClientRect();
          const rel = ((e.clientX - box.left) / box.width) * W;
          const i = Math.round(((rel - PAD.left) / plotW) * (series.length - 1));
          setHover(Math.min(series.length - 1, Math.max(0, i)));
        }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--yes)" stopOpacity="0.20" />
            <stop offset="100%" stopColor="var(--yes)" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {ticks.map((t, i) => (
          <g key={i}>
            <line className="grid-line" x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)} />
            <text className="axis-label" x={PAD.left + plotW + 6} y={y(t) + 4}>
              {credits(t)}
            </text>
          </g>
        ))}

        {/* Break-even. The one line on the chart that means something on its
            own, so it is drawn heavier than the grid and labelled. */}
        <line
          x1={PAD.left}
          x2={PAD.left + plotW}
          y1={zeroY}
          y2={zeroY}
          stroke="var(--muted)"
          strokeWidth="1"
          strokeDasharray="4 3"
        />
        {/* Labelled inside the plot, not in the axis gutter: "break even" is far
            wider than a number and ran past the panel edge out there. */}
        <text
          className="axis-label"
          x={PAD.left + 6}
          y={zeroY - 6}
          style={{ fill: 'var(--ink-2)' }}
        >
          break even
        </text>

        <path d={area} fill={`url(#${gradientId})`} />
        <path className="series" d={path} />

        {active && (
          <g>
            <line className="crosshair" x1={x(hover!)} x2={x(hover!)} y1={PAD.top} y2={PAD.top + plotH} />
            <circle className="dot" cx={x(hover!)} cy={y(active.value)} r="4.5" />
          </g>
        )}
        {!active && <circle className="dot" cx={x(series.length - 1)} cy={y(latest.value)} r="4.5" />}
      </svg>

      {active && (
        <div className="chart-tip" style={{ pointerEvents: 'none' }}>
          <b>{active.value < 0n ? '−' : '+'}{credits(active.value < 0n ? -active.value : active.value)}</b>
          {' after '}
          {active.question.length > 54 ? `${active.question.slice(0, 54)}…` : active.question}
        </div>
      )}
    </div>
  );
}
